import { Stack } from '@chakra-ui/react';
import { useLingui } from '@lingui/react/macro';

import type { ButtonProps } from '../Button';
import { TriangleAlertIcon, WifiOffIcon } from '../Icon';
import { ListEmpty } from '../ListEmpty';
import { Placeholder } from '../Placeholder';

type LoadFailedProps = {
  /** Повторить запрос. Обычно `refetch` из react-query. */
  onRetry?: () => void;
  /** Параметры действия для компактного или сенсорного контейнера. */
  retryButtonProps?: ButtonProps;
  /**
   * Размер роли, как у пустых состояний: `page` — экран целиком, `list` — список внутри
   * экрана, рядом с которым есть другое содержимое.
   */
  variant?: 'page' | 'list';
  /**
   * Ошибка сети, а не сервера: меняются иконка и текст. Отличать полезно, потому что
   * «проверьте соединение» человеку с упавшим вайфаем говорит, что делать, а при ошибке
   * сервера это был бы ложный совет.
   */
  offline?: boolean;
};

/**
 * «Не загрузилось» — третье состояние выдачи рядом с «идёт загрузка» и «здесь пусто».
 *
 * Его отсутствие было не дырой в оформлении, а **неправдой в интерфейсе**. Списки заявок,
 * сессий и друзей разбирали у запроса только `isLoading`. Когда запрос падал, загрузка
 * заканчивалась, список оставался пустым — и человек видел «Заявок пока нет»: продукт
 * утверждал, что на площадке пусто, хотя на самом деле не смог получить ответ. На холодном
 * старте, где каждая заявка на счету, это худшая из возможных ошибок: она выглядит как
 * приговор площадке, а не как сбой связи.
 *
 * Поэтому здесь не «что-то пошло не так», а всегда действие: повторить. Кнопка обязана
 * что-то делать — без `onRetry` она не рисуется вовсе, потому что кнопка, которая ничего не
 * меняет, хуже её отсутствия.
 *
 * Роли те же две, что и у пустых состояний, и по той же причине (см. `ListEmpty`):
 * `page` — экран целиком, `list` — список рядом с другим содержимым.
 */
export const LoadFailed = ({
  onRetry,
  retryButtonProps,
  variant = 'page',
  offline = false,
}: LoadFailedProps) => {
  const { t } = useLingui();

  const icon = offline ? WifiOffIcon : TriangleAlertIcon;
  const title = offline ? t`No connection` : t`Couldn't load this`;
  const description = offline
    ? t`Check your internet connection and try again.`
    : t`The request didn't go through. This is on us, not on you.`;

  if (variant === 'list') {
    return (
      <ListEmpty icon={icon} title={title} description={description}>
        {onRetry ? (
          <Placeholder.Button variant="outline" size="sm" {...retryButtonProps} onClick={onRetry}>
            {t`Try again`}
          </Placeholder.Button>
        ) : undefined}
      </ListEmpty>
    );
  }

  return (
    <Placeholder icon={icon}>
      <Placeholder.Title>{title}</Placeholder.Title>
      <Placeholder.Description>{description}</Placeholder.Description>
      {onRetry ? (
        <Stack direction={{ base: 'column', sm: 'row' }} gap={3}>
          <Placeholder.Button
            {...retryButtonProps}
            onClick={onRetry}
          >{t`Try again`}</Placeholder.Button>
        </Stack>
      ) : null}
    </Placeholder>
  );
};
